import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {MdBlock} from 'react-icons/md';
import { Eye, EyeOff, Stethoscope, LogIn } from 'lucide-react';
import { loginStart, loginSuccess, loginFailure, clearError } from '../../store/authSlice';
import { authAPI } from '../../api';
import MD5 from 'crypto-js/md5';
import { RootState } from '../../store/store';
import toast from 'react-hot-toast';

interface LoginFormInput {
  email?: string;
  password?: string;
}

export default function LoginPage() {
  const { register, handleSubmit, formState: { errors, isValid } } = useForm<LoginFormInput>({ mode: 'onChange' });
  const [showPassword, setShowPassword] = useState(false);
  const [isBlockedUser, setIsBlockedUser] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((s: RootState) => s.auth);
  const [showUpdatePasswordModal, setShowUpdatePasswordModal] = useState(false);
  const [tempEmail, setTempEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    if (error) dispatch(clearError());
  }, [dispatch]);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (MD5(newPassword).toString() === tempPassword) {
      toast.error('New password cannot be the same as temporary password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setUpdateLoading(true);
    try {
      const res = await authAPI.updatePassword({
        email: tempEmail,
        tempPassword: tempPassword,
        newPassword: MD5(newPassword).toString()
      });
      setShowUpdatePasswordModal(false);
      dispatch(loginSuccess(res.data));
      toast.success('Password updated successfully! 👋');
      navigate('/dashboard');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update password');
    } finally {
      setUpdateLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    setIsBlockedUser(false);
    dispatch(loginStart());
    try {
      const payload = { ...data, email: data.email?.toLowerCase(), password: MD5(data.password).toString() };
      const res = await authAPI.login(payload);

      if (res.data?.seller?.isBlock === true) {
        setIsBlockedUser(true);
        dispatch(loginFailure(''));
        return;
      }

      if (res.data?.isTemporaryPassword) {
        setTempEmail(payload.email);
        setTempPassword(payload.password);
        setShowUpdatePasswordModal(true);
        toast('Please update your password', { icon: '🔒' });
        dispatch(loginFailure('')); // stop button loading
        return;
      }

      dispatch(loginSuccess(res.data));
      toast.success('Welcome back! 👋');
      navigate('/dashboard');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Login failed';
      dispatch(loginFailure(msg));
      toast.error(msg);
    }
  };

  return (
    <div className="auth-bg" style={{ padding: '24px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 40%, #faf5ff 80%, #fff7ed 100%)' }}>
      <div style={{ width: '100%', maxWidth: '420px', animation: 'fadeIn 0.5s ease-out' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <h1 style={{ color: '#0f172a', fontSize: '32px', fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '4px' }}>
            Decso<span style={{ color: '#14b8a6' }}>plus</span>
          </h1>
          <p style={{ color: '#64748b', fontSize: '15px', fontWeight: '500' }}>Seller Portal</p>
        </div>

        {/* Card */}
        <div className="auth-card" style={{
            background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)',
            borderRadius: '24px', padding: '25px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
            border: '1px solid rgba(255,255,255,0.6)'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', marginBottom: '8px' }}>Sign In</h2>
          <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '32px' }}>Access your seller dashboard</p>

          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label className="form-label">Email Address</label>
              <input
                type="email"
                className="form-input"
                placeholder="seller@example.com"
                style={{ height: '48px' }}
                {...register('email', { required: 'Enter your email address (e.g., user@example.com).', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' } })}
              />
              {errors.email && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px' }}>{errors.email.message}</p>}
            </div>

            <div>
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="••••••••"
                  style={{ paddingRight: '48px', height: '48px' }}
                  {...register('password', { required: 'Password cannot be empty.' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  {showPassword ?   <Eye size={20} color="#94a3b8" /> : <EyeOff size={20} color="#94a3b8" />}
                </button>
              </div>
              {errors.password && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px' }}>{errors.password.message}</p>}
              {isBlockedUser && (
                <p style={{ color: '#ef4444', fontSize: '13px', fontWeight: '600', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <MdBlock size={18} /> You were blocked. Please contact support.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-8px' }}>
              <Link to="/forgot-password" onClick={() => dispatch(clearError())} style={{ color: '#14b8a6', fontSize: '14px', fontWeight: '700', textDecoration: 'none' }}>
                Forgot Password?
              </Link>
            </div>

            {error && (
              <div style={{ 
                padding: '10px 14px', 
                background: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '8px', 
                color: '#dc2626', 
                fontSize: '12px', 
                fontWeight: '600', 
                textAlign: 'center', 
                animation: 'fadeIn 0.3s', 
                marginTop: '-4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1.5'
              }}>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="btn-primary" 
              style={{ 
                width: '100%', 
                justifyContent: 'center', 
                height: '52px', 
                fontSize: '16px', 
                borderRadius: '12px', 
                marginTop: '8px',
                opacity: (!isValid || loading) ? 0.6 : 1,
                cursor: (!isValid || loading) ? 'not-allowed' : 'pointer'
              }} 
              disabled={!isValid || loading}
            >
              {loading ? <div className="spinner" style={{ width: '20px', height: '20px' }} /> : <><LogIn size={20} /> Sign In</>}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: '32px', fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
            Don't have an account?{' '}
            <Link to="/register" onClick={() => dispatch(clearError())} style={{ color: '#14b8a6', fontWeight: '800', textDecoration: 'none' }}>
              Register as Seller
            </Link>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Update Password Modal */}
      {showUpdatePasswordModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '24px'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', padding: '40px',
            width: '100%', maxWidth: '440px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', marginBottom: '8px' }}>Update Password</h2>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '32px' }}>For your security, please create a new password to continue.</p>

            <form onSubmit={handleUpdatePassword} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label className="form-label">New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="••••••••"
                    style={{ paddingRight: '48px', height: '48px' }}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    {showPassword ? <EyeOff size={20} color="#94a3b8" /> : <Eye size={20} color="#94a3b8" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="form-label">Confirm Password</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="••••••••"
                  style={{ height: '48px' }}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', height: '52px', fontSize: '16px', borderRadius: '12px', marginTop: '8px' }} disabled={updateLoading}>
                {updateLoading ? <div className="spinner" style={{ width: '20px', height: '20px' }} /> : 'Update & Continue'}
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
